import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clearSessionStorage, getNewToken, postRequest } from '../services/httpservice';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import File from "../assets/File.svg"
import { Link, useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import Tooltip from '@mui/material/Tooltip';
import { apiURL } from '../constants/apiUrlConstants';
import KuwaitiDinarFormat from './KuwaitiDinarFormat';
import Loader from './Loader';
import TransactionReceiptModel from './TransactionReceiptModel';
import axios, { AxiosError } from 'axios';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Typography } from '@mui/material';

interface TransTableProps {
    civilId: string | null;
}

interface Transaction {
    amount: string;
    quickPay: string;
    paymentDate: string;
    receiptNo: string;
    authCode: string;
    paymentStatus: string;
    ottuOrderNo: string;
    ottuTranId: string;
    ecTransPayId: string;
    accountNo:string;
    invoiceNo:string;
}

interface ApiResponse {
    civilId: string;
    status: "S";
    message: string;
    transactionHistory: Transaction[];
}

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

export interface TransactionReceipt {
    civilId: string;
    status: string;
    message: string;
    custName: string;
    custAddress: string;
    custMobile: string;
    receiptDate: string;
    receiptNo: string;
    chargesAmount: string;
    subTotal: string;
    totalAmount: string;
    ottuTranId: string;
    InstallmentDetails: InstallmentDetails[]
}

export interface InstallmentDetails {
    loanAccountNo: string;
    installmentNo: string;
    dueDate: string;
    installmentAmount: string;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            {/* <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton> */}
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            {/* <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton> */}
        </Box>
    );
}

const getStatusColor = (status: string) => {
    switch (status) {
        case 'Success':
            return '#00A62E';
        case 'Fail':
            return '#FF0000';
        case 'Pending':
            return '#FE7D0D';
        default:
            return '#0067B2';
    }
};

const TransactionTable: React.FC<TransTableProps> = ({ civilId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionReceipt | null>(null);
    const [toastCloseInMs, setToastCloseInMs] = useState<number>(0);

    useEffect(() => {
        if (civilId) {
            const msToast = process.env.REACT_APP_TOAST_CLOSE_IN_SECONDS;
            if(msToast) {
            setToastCloseInMs(parseInt(msToast));
            }
            getTransactionDetails();
        }
    }, [civilId]);

    const getTransactionDetails = async () => {
        setIsLoading(true);
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                navigate('/');
                return;
            }

            if (accessToken) {
                const response = await postRequest(
                    process.env.REACT_APP_API_URL + apiURL.GET_TRANSACTIONS, {
                    civilId: civilId,
                }
                );
                setIsLoading(false);
                if (response == null) {
                    toast.dismiss();
                    toast.error(process.env.REACT_APP_ERROR_MSG);
                    throw new Error(`HTTP error! Status`);
                }
                if (response.status === 401) {
                    toast.error('Session expired');
                    clearSessionStorage();
                    setTimeout(() => {
                        navigate('/');
                      }, 2000);
                    return;
                }
                if(response.data=== '') {
                    toast.dismiss();
                    toast.error(process.env.REACT_APP_ERROR_MSG);
                    return;
                }

                
                const data = response.data;
                setTransactionDetails(data);
                setTransactions(data.transactionHistory)
                setIsLoading(false);
                getNewToken();
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError;
                if (axiosError.response) {
                    if (axiosError?.response.status == 401) {
                        toast.error('Session expired');
                        clearSessionStorage();
                        setTimeout(() => {
                            navigate('/');
                          }, 2000);
                    }else {
                        toast.error(process.env.REACT_APP_ERROR_MSG);
                    }
                }
            }
        }
    }

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - transactions?.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getTransactionReceiptDetails = async (receiptNo: string) => {
        if (receiptNo == null || receiptNo == "") {
            toast.error("Receipt number should not be empty.");
            return;
        }
        setIsLoading(true);
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                navigate('/');
                return;
            }

            if (accessToken) {
                const response = await postRequest(
                    process.env.REACT_APP_API_URL + apiURL.GET_TRANSACTION_RECEIPT, {
                    civilId: civilId,
                    receiptNo: receiptNo
                }
                );
                setIsLoading(false);
                if (response == null) {
                    toast.dismiss();
                    toast.error(process.env.REACT_APP_ERROR_MSG);
                    return;
                }
                if (response.status === 401) {
                    toast.error('Session expired');
                    clearSessionStorage();
                    setTimeout(() => {
                        navigate('/');
                      }, 2000);
                    return;
                }
                if(response.data=== '') {
                    toast.dismiss();
                    toast.error(process.env.REACT_APP_ERROR_MSG);
                    return;
                }

              
                const data = response.data;
                const normalizedReceipt = {
                    ...data,
                    InstallmentDetails: data.installmentDetails || data.InstallmentDetails || [],
                };
                setSelectedTransaction(normalizedReceipt);
                setIsLoading(false);
                getNewToken();
                handleOpen();
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                 toast.dismiss();
                const axiosError = error as AxiosError;
                if (axiosError.response) {
                    if (axiosError?.response.status == 401) {
                        toast.error('Session expired');
                        clearSessionStorage();
                        setTimeout(() => {
                            navigate('/');
                          }, 2000);
                    }else {
                        toast.error(process.env.REACT_APP_ERROR_MSG);
                    }
                }
            }
        }
    }

    return (
      <>
        {isLoading && <Loader />}
        <TableContainer className="centerAlign-Table" component={Paper} sx={{ width: "100%"  ,   pt: 0,}}>
          <Table
          
            sx={{ minWidth: { md: 500 } }} 
            size="small"
            aria-label="Transaction Table"
          >
            <TableHead>
              <TableRow
                className="tableRow"
                sx={{
                  "& .MuiTableCell-root": {
                    width: "127px",
                    height: "106px",
                    opacity: 1,
                    borderRight: "1px solid #E0E0E0",
                    borderLeft: "1px solid #E0E0E0",
                    pt: "30px",
                    pb: "30px",
                  color:"rgba(0, 16, 75, 0.87)",
                    fontWeight: 600,
                    fontSize: "13px",
                    lineHeight: "163%",
                    letterSpacing: "0%",
                    textAlign: "center",
                    verticalAlign: "middle",
                  },
                }}
              >
                {/* <TableCell>{t('transactions.transactionId')}</TableCell>
                            <TableCell>{t('transactions.receiptNumber')}</TableCell> */}
                <TableCell>{t("transactions.account")}</TableCell>
                <TableCell>{t("transactions.orderinvoice")}</TableCell>
                <TableCell>{t("transactions.amount")}</TableCell>
                {/* <TableCell>{t('transactions.partOfQuickPay').split('\n').map((l: string, i: number) => i === 0 ? l : [<br key={i}/>,l])}</TableCell> */}
                <TableCell>{t("transactions.paymentDate")}</TableCell>
                <TableCell>{t("transactions.receiptNumber")}</TableCell>
                <TableCell>{t("transactions.authorisationCode")}</TableCell>
                <TableCell>{t("transactions.status")}</TableCell>
                <TableCell>{t("transactions.receipt")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="transactionTable-tablebody" sx={{
    "& .MuiTableRow-root": {
      height: "54px",
    },
    "& .MuiTableCell-root": {
      height: "54px",
      padding: "0 16px",
      verticalAlign: "middle",
      boxSizing: "border-box",
    },
  }}>
              {transactions &&
                (rowsPerPage > 0
                  ? transactions &&
                    transactions.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : transactions
                ).map((row, index) => (
                  <TableRow className="tableRow" key={index}>
                    <TableCell>{row.accountNo}</TableCell>

                    <TableCell>{row.invoiceNo}</TableCell>
                    <TableCell>
                      <KuwaitiDinarFormat amount={Number(row.amount)} />
                    </TableCell>
                    {/* <TableCell>{row.quickPay}</TableCell> */}
                    <TableCell>{row.paymentDate}</TableCell>
                    <TableCell>{row.receiptNo}</TableCell>
                    <TableCell>{row.authCode}</TableCell>
                    <TableCell className="transaction-cell-status">
                      <Box sx={{ color: getStatusColor(row.paymentStatus) }}>
                        {row.paymentStatus === "Success" && (
                          <>
                            <CheckCircleOutlineIcon />
                            {t("transactions.paymentStatus.success")}
                          </>
                        )}

                        {row.paymentStatus === "Fail" && (
                          <>
                            <CancelOutlinedIcon />
                            {t("transactions.paymentStatus.fail")}
                          </>
                        )}

                        {row.paymentStatus === "Pending" && (
                          <>
                            <AccessTimeOutlinedIcon />
                            {t("transactions.paymentStatus.pending")}
                          </>
                        )}

                        {row.paymentStatus === "Processing" && (
                          <>
                            <CheckCircleOutlineIcon
                              sx={{ color: "success.main" }}
                            />
                            <span style={{ color: "#2e7d32" }}>
                              {t("transactions.paymentStatus.processing")}
                            </span>
                          </>
                        )}
                        {/* <span>{row.paymentStatus}</span> */}
                      </Box>
                    </TableCell>
                    <TableCell className="transaction-cell-receipt">
                      {row.paymentStatus === "Success" ? (
                        <Tooltip title={t("transactions.viewReceipt")}>
                          <img
                            src={File}
                            alt="Logo"
                            onClick={() => {
                              getTransactionReceiptDetails(row.receiptNo);
                            }}
                            style={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      ) : (
                        <img
                          src={File}
                          alt="Logo"
                          style={{
                            cursor: "not-allowed",
                            filter: "grayscale(100%)",
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {transactions.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#888', fontSize: '14px' }}>
                    {t('transactions.noDataFound', 'No data found')}
                  </TableCell>
                </TableRow>
              )}
              {emptyRows > 0 && (
                <TableRow className="transactionTable-emptyRow">
                  <TableCell colSpan={10} />
                </TableRow>
              )}
            </TableBody>
            {/* <TableBody className="transactionTable-tablebody">
  <TableRow className="tableRow">

    <TableCell>TRX100245</TableCell>
    <TableCell>43261234</TableCell>
    <TableCell>120.500 KD</TableCell>
    <TableCell>Yes</TableCell>
    <TableCell>01 Oct 2025</TableCell>
    <TableCell>-23lkndl</TableCell>
    <TableCell>AUTH78945</TableCell>
<TableCell className="transaction-cell-status">
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      color: "#4CAF50",
      fontWeight: 500,
      lineHeight: 1,
    }}
  >
    <CheckCircleOutlineIcon
      sx={{
        fontSize: 20,
        verticalAlign: "middle",
      }}
    />
    <Typography
      component="span"
      sx={{
        fontSize: "14px",
        lineHeight: 1,
      }}
    >
      Success
    </Typography>
  </Box>
</TableCell>

    <TableCell className='transaction-cell-receipt'>
                                    { 
                                        <Tooltip title={t('transactions.viewReceipt')}>
                                            <img
                                                src={File}
                                                alt="Logo"
                                                // onClick={() => {
                                                //     getTransactionReceiptDetails(row.receiptNo)
                                                // }}
                                                   onClick={() => {
        handleOpen();
    }}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </Tooltip>
                                    //  : (
                                    //     <img
                                    //         src={File}
                                    //         alt="Logo"
                                    //         style={{ cursor: 'not-allowed', filter: 'grayscale(100%)' }}
                                    //     />
                                    // )
                                    }
                                </TableCell>

  </TableRow>
    <TableRow className="tableRow">

    <TableCell>TRX100245</TableCell>
    <TableCell>43261234</TableCell>
    <TableCell>120.500 KD</TableCell>
    <TableCell>Yes</TableCell>
    <TableCell>01 Oct 2025</TableCell>
    <TableCell>-23lkndl</TableCell>
    <TableCell>AUTH78945</TableCell>
<TableCell className="transaction-cell-status">
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      color: "#e03112",
      fontWeight: 500,
      lineHeight: 1,
             ml: "-12px"
    }}
  >
    <CancelOutlinedIcon
      sx={{
        fontSize: 20,
        verticalAlign: "left",
      }}
    />
    <Typography
      component="span"
      sx={{
        fontSize: "14px",
        lineHeight: 1,
      }}
    >
      Failed
    </Typography>
  </Box>
</TableCell>

    <TableCell className='transaction-cell-receipt'>
                                    { 
                                        <Tooltip title={t('transactions.viewReceipt')}>
                                            <img
                                                src={File}
                                                alt="Logo"
                                                // onClick={() => {
                                                //     getTransactionReceiptDetails(row.receiptNo)
                                                // }}
                                                   onClick={() => {
        handleOpen();
    }}
                                                     style={{
        cursor: "not-allowed",
        opacity: 0.4,
        filter: "grayscale(100%)",
        pointerEvents: "none",
      }}
                                            />
                                        </Tooltip>
                                    //  : (
                                    //     <img
                                    //         src={File}
                                    //         alt="Logo"
                                    //         style={{ cursor: 'not-allowed', filter: 'grayscale(100%)' }}
                                    //     />
                                    // )
                                    }
                                </TableCell>

  </TableRow>
</TableBody> */}
          </Table>
        </TableContainer>
        <BootstrapDialog
          className="receiptModal"
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <TransactionReceiptModel
            transaction={selectedTransaction}
            onClose={handleClose}
          />
        </BootstrapDialog>

        <ToastContainer
          position="top-right"
          newestOnTop
          limit={1}
          autoClose={toastCloseInMs}
        />
        <Box sx={{ width: "100%", mt: 2 }}>
          {/* ROW 1 */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* empty */}
            <Box />

            {/* empty */}
            <Box />

            {/* PAGINATION (right) */}
            <Box
              className="transactionTable-footer"
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <TablePagination
                component="div"
                count={transactions?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                labelRowsPerPage=""
                rowsPerPageOptions={[]}
              />
            </Box>
          </Box>

          {/* ROW 2 */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              alignItems: "center",
              mt: 1,
            }}
          >
            {/* BACK */}
            <Box
              className="trns-backBoxContainer"
              sx={{ display: { xs: "none", sm: "flex" } }}
            >
              <Link className="trns-backBoxContainerLink" to={`/home/deals`}>
                {t("common.back")}
              </Link>
            </Box>

            {/* empty middle space */}
            <Box />

            {/* NOTE aligned right side but same row logic */}
         { /*  <Typography
              className="quickPayTypo"
              sx={{
                fontSize: "11.68px",

                textAlign: "right",
              }}
            >
              {t("transactions.emailNote")}
            </Typography>*/}
          </Box>
        </Box>
      </>
    );
}

export default TransactionTable;